<template>
  <div class="analytics">
    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <div 
        v-for="(stat, index) in summaryStats" 
        :key="index" 
        class="bg-white p-6 rounded-lg shadow-sm"
      >
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-500 text-sm">{{ stat.title }}</p>
            <h3 class="text-2xl font-bold mt-1">{{ stat.value }}</h3>
          </div>
          <div 
            :class="stat.iconBg" 
            class="w-12 h-12 rounded-full flex items-center justify-center"
          >
            <i :class="[stat.icon, stat.iconColor]"></i>
          </div>
        </div>
        <p 
          :class="stat.changeColor" 
          class="mt-2 flex items-center text-sm font-medium"
        >
          <i :class="stat.changeIcon" class="mr-1"></i>
          {{ stat.change }}
        </p>
      </div>
    </div>
    
    <!-- Charts -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <!-- Payment Status Distribution -->
      <div class="bg-white p-6 rounded-lg shadow-sm">
        <h3 class="text-lg font-medium mb-4">Payment Status Distribution</h3>
        <div class="h-64">
          <canvas ref="paymentChartRef"></canvas>
        </div>
      </div>
      
      <!-- Property Price Distribution -->
      <div class="bg-white p-6 rounded-lg shadow-sm">
        <h3 class="text-lg font-medium mb-4">Property Price Distribution</h3>
        <div class="h-64">
          <canvas ref="priceChartRef"></canvas>
        </div>
      </div>
    </div>
    
    <!-- Property Location Heatmap -->
    <div class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <h3 class="text-lg font-medium mb-4">Property Location Distribution</h3>
      <div id="heatmap" class="h-64"></div>
    </div>
    
    <!-- Recent Transactions -->
    <div class="bg-white p-6 rounded-lg shadow-sm">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium">Recent Transactions</h3>
        <button class="text-primary-600 hover:text-primary-700 text-sm font-medium">
          View All
        </button>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Property
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(transaction, index) in recentTransactions" :key="index">
              <td class="px-6 py-4 whitespace-nowrap">
                {{ transaction.property }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap font-medium">
                ${{ transaction.amount.toLocaleString() }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  :class="{
                    'bg-green-100 text-green-800': transaction.status === 'Completed',
                    'bg-yellow-100 text-yellow-800': transaction.status === 'Pending',
                    'bg-red-100 text-red-800': transaction.status === 'Failed'
                  }"
                  class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ transaction.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-gray-500">
                {{ transaction.date }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { usePropertiesStore } from '../stores/properties';
import Chart from 'chart.js/auto';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import Heatmap from 'ol/layer/Heatmap';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import OSM from 'ol/source/OSM';

const propertiesStore = usePropertiesStore();
const paymentChartRef = ref(null);
const priceChartRef = ref(null);
let paymentChart = null;
let priceChart = null;
let heatmapMap = null;

// Summary statistics
const summaryStats = computed(() => {
  // Use backend statistics if available, or computed values as fallback
  const stats = propertiesStore.statistics.total > 0 
    ? propertiesStore.statistics 
    : propertiesStore.propertyStatistics;
  
  return [
    {
      title: 'Total Properties',
      value: stats.total,
      icon: 'fas fa-building',
      iconBg: 'bg-primary-100',
      iconColor: 'text-primary-600',
      change: '8% increase from last month',
      changeColor: 'text-green-600',
      changeIcon: 'fas fa-arrow-up'
    },
    {
      title: 'Paid Properties',
      value: stats.paid,
      icon: 'fas fa-check-circle',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      change: '5% increase from last month',
      changeColor: 'text-green-600',
      changeIcon: 'fas fa-arrow-up'
    },
    {
      title: 'Unpaid Properties',
      value: stats.unpaid,
      icon: 'fas fa-exclamation-circle',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      change: '3% decrease from last month',
      changeColor: 'text-green-600',
      changeIcon: 'fas fa-arrow-down'
    }
  ];
});

// Recent transactions based on our Kenya property data
const recentTransactions = [
  { property: 'Luxury Villa in Karen', amount: 25000000, status: 'Completed', date: '2025-03-28' },
  { property: 'Modern Apartment in Westlands', amount: 12500000, status: 'Pending', date: '2025-03-25' },
  { property: 'Cozy Home in Lavington', amount: 18000000, status: 'Completed', date: '2025-03-22' },
  { property: 'Penthouse in Upperhill', amount: 32000000, status: 'Completed', date: '2025-03-20' },
  { property: 'Family Home in Muthaiga', amount: 28000000, status: 'Completed', date: '2025-03-15' }
];

onMounted(async () => {
  // Fetch properties and statistics
  await Promise.all([
    propertiesStore.fetchProperties(),
    propertiesStore.fetchStatistics()
  ]);
  
  // Initialize charts after data is loaded
  initPaymentChart();
  initPriceChart();
  initHeatmap();
});

// Initialize payment status distribution chart
const initPaymentChart = () => {
  // Use backend statistics if available, or computed values as fallback
  const stats = propertiesStore.statistics.total > 0 
    ? propertiesStore.statistics 
    : propertiesStore.propertyStatistics;
  
  if (paymentChart) {
    paymentChart.destroy();
  }
  
  const ctx = paymentChartRef.value.getContext('2d');
  paymentChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Paid', 'Unpaid'],
      datasets: [{
        data: [stats.paid, stats.unpaid],
        backgroundColor: [
          '#10B981',  // Green for paid
          '#EF4444'   // Red for unpaid
        ],
        borderWidth: 0
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }
  });
};

// Initialize price distribution chart
const initPriceChart = () => {
  const properties = propertiesStore.properties;
  
  // Group properties by price range (in millions for Kenyan properties)
  const priceRanges = {
    '0-5M': 0,
    '5M-10M': 0,
    '10M-15M': 0,
    '15M-25M': 0,
    '25M+': 0
  };
  
  properties.forEach(property => {
    const price = property.price;
    if (price < 5000000) {
      priceRanges['0-5M']++;
    } else if (price < 10000000) {
      priceRanges['5M-10M']++;
    } else if (price < 15000000) {
      priceRanges['10M-15M']++;
    } else if (price < 25000000) {
      priceRanges['15M-25M']++;
    } else {
      priceRanges['25M+']++;
    }
  });
  
  if (priceChart) {
    priceChart.destroy();
  }
  
  const ctx = priceChartRef.value.getContext('2d');
  priceChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: Object.keys(priceRanges),
      datasets: [{
        label: 'Number of Properties',
        data: Object.values(priceRanges),
        backgroundColor: '#0EA5E9',
        borderWidth: 0
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        }
      }
    }
  });
};

// Initialize heatmap
const initHeatmap = () => {
  const properties = propertiesStore.properties;
  
  // Create features for heatmap
  const features = properties.map(property => {
    return new Feature({
      geometry: new Point(fromLonLat([property.longitude, property.latitude])),
      weight: property.price / 10000000  // Weight by price (adjusted for Kenyan property values)
    });
  });
  
  // Create vector source with features
  const vectorSource = new VectorSource({
    features: features
  });
  
  // Create heatmap layer
  const heatmapLayer = new Heatmap({
    source: vectorSource,
    blur: 15,
    radius: 10,
    gradient: ['#FFEDA0', '#FEB24C', '#FD8D3C', '#FC4E2A', '#E31A1C', '#BD0026', '#800026']
  });
  
  // Create map
  heatmapMap = new Map({
    target: 'heatmap',
    layers: [
      new TileLayer({
        source: new OSM()
      }),
      heatmapLayer
    ],
    view: new View({
      center: fromLonLat([36.8219, -1.2921]), // Nairobi, Kenya
      zoom: 12
    })
  });
};
</script>
